import { ContentStudioCard } from "@/components/content-studio-card";
import { useAuth } from "@/lib/auth";
import {
  Album,
  BookOpen,
  Brain,
  FileText,
  FolderTree,
  Headphones,
  Map,
  Quote,
  Star,
  Target,
  UtensilsCrossed,
  Video,
} from "lucide-react";

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-medium text-foreground">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      )}
    </div>
  );
}

// Horoscope endpoints are server-gated to these roles (see horoscope.controller.ts).
// Coach is excluded, so hide the card to avoid a 403 "unable to load entries".
const HOROSCOPE_ROLES = ["super_admin", "admin", "content_manager"];

export default function ContentStudio() {
  const { user } = useAuth();
  const canHoroscope = !!user?.roles?.some((r) =>
    HOROSCOPE_ROLES.includes(r),
  );

  const cards = [
    {
      icon: Headphones,
      title: "Audio",
      description: "Upload and manage guided meditations and audio content",
      href: "/studio/audio",
      dataTestId: "card-type-audio",
    },
    {
      icon: Video,
      title: "Video",
      description: "Upload and manage video content with transcoding",
      href: "/studio/video",
      dataTestId: "card-type-video",
    },
    {
      icon: FileText,
      title: "Blog",
      description: "Write articles and blog posts with rich content",
      href: "/studio/blogs",
      dataTestId: "card-type-blog",
    },
    {
      icon: UtensilsCrossed,
      title: "Recipes",
      description:
        "Create and manage recipe content with ingredients and steps",
      href: "/studio/recipes",
      dataTestId: "card-type-recipe",
    },
    {
      icon: Quote,
      title: "Affirmations",
      description: "Create and manage inspirational affirmation quotes",
      href: "/studio/affirmations",
      dataTestId: "card-type-affirmation",
    },
    {
      icon: Star,
      title: "Horoscope",
      description: "Zodiac sign management and daily horoscope entries",
      href: "/studio/horoscope",
      dataTestId: "card-horoscope",
    },
    {
      icon: BookOpen,
      title: "Workbooks",
      description: "Build reusable workbook content for journeys",
      href: "/studio/workbooks",
      dataTestId: "card-type-workbook",
    },
    {
      icon: Target,
      title: "Challenges",
      description: "Create challenge prompts for workbooks",
      href: "/studio/challenges",
      dataTestId: "card-type-challenge",
    },
  ];

  const organizationCards = [
    {
      icon: FolderTree,
      title: "Categories",
      description: "Top-level content organisation",
      href: "/studio/categories",
      dataTestId: "card-taxonomy-categories",
    },
    {
      icon: Brain,
      title: "Tag Groups",
      description: "Mood, feelings, shifts and tag management",
      href: "/studio/tag-groups",
      dataTestId: "card-taxonomy-tag-groups",
    },
    {
      icon: Album,
      title: "Albums",
      description: "Group content into curated collections",
      href: "/studio/albums",
      dataTestId: "card-taxonomy-albums",
    },
  ];

  const programsCards = [
    {
      icon: Map,
      title: "Journeys",
      description: "Build multi-day guided journey programs",
      href: "/studio/journeys",
      dataTestId: "card-program-journeys",
    },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div>
        <h1
          className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight"
          data-testid="text-page-title"
        >
          Content Studio
        </h1>
        <p
          className="text-muted-foreground mt-1"
          data-testid="text-page-description"
        >
          Design emotional wellness experiences for the Evooluir app
        </p>
      </div>
      <section data-testid="section-organization">
        <SectionHeader
          title="Organisation"
          description="Taxonomy and content grouping tools"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizationCards.map((card) => (
            <ContentStudioCard
              key={card.href}
              href={card.href}
              icon={card.icon}
              title={card.title}
              description={card.description}
              dataTestId={card.dataTestId}
            />
          ))}
        </div>
      </section>
      <section data-testid="section-content-types">
        <SectionHeader
          title="Content Types"
          description="Core content modules for the Evooluir app"
        />
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {cards
            .filter(
              (card) => card.href !== "/studio/horoscope" || canHoroscope,
            )
            .map((card) => (
              <ContentStudioCard
                key={card.href}
                href={card.href}
                icon={card.icon}
                title={card.title}
                description={card.description}
                dataTestId={card.dataTestId}
              />
            ))}
        </div>
      </section>
      <section data-testid="section-programs">
        <SectionHeader
          title="Programs"
          description="Multi-day programs and ritual orchestration"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {programsCards.map((card) => (
            <ContentStudioCard
              key={card.href}
              href={card.href}
              icon={card.icon}
              title={card.title}
              description={card.description}
              dataTestId={card.dataTestId}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
